import {
  ApolloQueryResult,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
} from '@apollo/client';
import { useCallback } from 'react';

import useQuery from 'hooks/graphql/useQuery';

export type LoadMore<D, V> = (
  reload: boolean,
  variables: V,
  forceConnection?: string
) => Promise<D>;

function mergeEdges<T extends { edges: any[]; pageInfo: any }>({
  previous,
  current,
  reload,
  prepend,
}: {
  previous: T;
  current: T;
  reload: boolean;
  prepend: boolean;
}) {
  const newEdges = current.edges;
  const { pageInfo } = current;
  let edges = [];

  if (reload) {
    edges = [...newEdges];
  } else if (previous) {
    edges = prepend
      ? [...newEdges, ...previous.edges]
      : [...previous.edges, ...newEdges];
  } else {
    edges = newEdges;
  }
  return { ...previous, ...current, edges, pageInfo };
}

function mergeNodes<T extends { nodes: any[]; pageInfo: any }>({
  previous,
  current,
  reload,
  prepend,
}: {
  previous: T;
  current: T;
  reload: boolean;
  prepend: boolean;
}) {
  const newNodes = current.nodes;
  const { pageInfo } = current;
  let nodes = [];

  if (reload) {
    nodes = [...newNodes];
  } else if (previous) {
    nodes = prepend
      ? [...newNodes, ...previous.nodes]
      : [...previous.nodes, ...newNodes];
  } else {
    nodes = newNodes;
  }
  return { ...previous, ...current, nodes, pageInfo };
}

function mergeResults({
  previous,
  current,
  reload,
  connections,
  prepend,
}: {
  previous: any;
  current: any | undefined;
  reload: boolean;
  connections: string[];
  prepend: boolean;
}): any {
  const result: { [key: string]: any } = {};

  if (previous === null || current === null) {
    return current;
  }
  if (Array.isArray(current) || Array.isArray(previous)) {
    return current;
  }
  if (typeof current === 'object') {
    if ((current as any).edges) {
      if (connections.includes((current as any).__typename)) {
        return mergeEdges({ previous, current, reload, prepend });
      }
      return current;
    }
    if (current.nodes) {
      if (connections.includes(current.__typename)) {
        return mergeNodes({ previous, current, reload, prepend });
      }
      return previous;
    }

    Object.keys(current).forEach(key => {
      result[key] = mergeResults({
        previous: (previous as any)?.[key],
        current: (current as any)[key],
        reload,
        connections,
        prepend,
      });
    });

    return { ...previous, ...result };
  }
  return current;
}

export interface WithRelayPagination<D, V> {
  loadMore: LoadMore<ApolloQueryResult<D>, Partial<V>>;
}

export default function usePaginatedQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: TypedDocumentNode<TData, TVariables>,
  options: QueryHookOptions<TData, TVariables> &
    (
      | { connection: string; connections?: never }
      | { connections: string[]; connection?: never }
    ) & {
      prepend?: boolean;
    }
): QueryResult<TData, TVariables> & WithRelayPagination<TData, TVariables> {
  const { connection, connections, prepend, ...rest } = options;
  const data = useQuery(query, rest);
  const { fetchMore, variables } = data;

  const loadMore = useCallback(
    async (
      reload: boolean,
      loadMoreVariables: Partial<TVariables>,
      forceConnection?: string
    ) =>
      fetchMore({
        // We don't need to add query again here and that can lead to unwanted bugs: https://github.com/apollographql/apollo-client/issues/6354#issuecomment-664879778
        // https://www.apollographql.com/docs/react/pagination/core-api/#:~:text=executes%20a%20query%20with%20the%20exact%20same%20shape%20and%20variables%20as%20your%20original%20query
        variables: { ...variables, ...loadMoreVariables },
        updateQuery: (previousResult, { fetchMoreResult }) =>
          mergeResults({
            previous: previousResult as TData,
            current: fetchMoreResult as TData,
            reload,
            connections: forceConnection
              ? [forceConnection]
              : connections || [connection],
            prepend: Boolean(prepend),
          })!,
      }),
    [fetchMore, variables, connections, connection, prepend]
  );

  return {
    ...data,
    loadMore,
  };
}
